'use client';

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { Circle } from 'lucide-react';
import { cn } from 'v5/platform/dom/cn';
import { focusRings } from './_common/focusRing';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const radioGroupItemVariants = cva(focusRings.focus.visible, {
  variants: {
    variant: {
      radio:
        'aspect-square h-4 w-4 rounded-full border border-border-default text-fg-default disabled:cursor-not-allowed disabled:opacity-50',
      /** A choice that carries its own content (an icon, a title, a description) instead of a dot. */
      card: 'flex items-start gap-3 rounded-lg border border-border-default p-3 text-left transition-colors hocus:bg-bg-subtle data-[state=checked]:border-border-accent-emphasis data-[state=checked]:bg-bg-accent disabled:cursor-not-allowed disabled:opacity-50'
    }
  },
  defaultVariants: { variant: 'radio' }
});

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> &
    VariantProps<typeof radioGroupItemVariants>
>(({ className, variant, children, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupItemVariants({ variant }), className)}
      {...props}
    >
      {variant === 'card' ? (
        children
      ) : (
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <Circle className="h-2.5 w-2.5 fill-current text-current" />
        </RadioGroupPrimitive.Indicator>
      )}
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
